import { useEffect, useState } from 'react'
import { createConsumer } from '@rails/actioncable'

export type Discipline = 'all' | 'cycling' | 'running' | 'walking'

export interface RankedArtist {
  id: string
  name: string
  image_url: string | null
  play_count: number
}

export interface RankedInstructor {
  id: string
  name: string
  image_url: string | null
  ride_count: number
}

export interface NamedCount {
  name: string
  count: number
}

export interface OverplayedSong {
  title: string
  artists: string[]
  ride_count: number
  album_image_url: string | null
}

export interface VersatileArtist {
  id: string
  name: string
  image_url: string | null
  genre_count: number
}

export interface ActivityEntry {
  id: string
  title: string
  instructor_name: string | null
  synced_at: string
  song_count: number
  fitness_discipline: Exclude<Discipline, 'all'>
}

export interface DisciplineStats {
  totals: {
    rides: number
    songs: number
    artists: number
    albums: number
    instructors: number
    one_hit_artists: number
    total_ride_hours: number
    avg_songs_per_ride: number
    explicit_percent: number
  }
  scrape_progress: {
    rides_synced: number
    rides_detailed: number
    percent: number
  }
  top_artists: RankedArtist[]
  top_instructors: RankedInstructor[]
  genres: NamedCount[]
  ride_types: NamedCount[]
  most_overplayed_song: OverplayedSong | null
  most_versatile_artist: VersatileArtist | null
  recent_activity: ActivityEntry[]
  last_synced_at: string | null
}

export type DashboardData = Record<Discipline, DisciplineStats>

interface DashboardMessage {
  // Omitted on most broadcasts -- the broadcaster throttles the expensive
  // full recompute to at most once every ~10s, independent of how often
  // individual rides finish syncing. Always present on the initial
  // subscribe transmit.
  stats?: DashboardData
  activity: ActivityEntry | null
}

const ACTIVITY_FEED_LIMIT = 15

// A single shared consumer for the module's lifetime -- React StrictMode's
// double-invoke of effects is handled by unsubscribing on cleanup, not by
// avoiding a second createConsumer call.
const consumer = createConsumer('/cable')

export function useDashboardChannel() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [activity, setActivity] = useState<ActivityEntry[]>([])
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: 'DashboardChannel' },
      {
        connected: () => setConnected(true),
        disconnected: () => setConnected(false),
        received: (message: DashboardMessage) => {
          if (message.stats) setData(message.stats)

          if (message.activity) {
            const entry = message.activity
            setActivity((prev) => (prev.some((e) => e.id === entry.id) ? prev : [entry, ...prev].slice(0, ACTIVITY_FEED_LIMIT)))
          } else if (message.stats && message.stats.all.recent_activity.length > 0) {
            setActivity(message.stats.all.recent_activity)
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  return { data, activity, connected }
}
